<script setup lang="ts">
import { ref, watch } from 'vue';
import { useRouter } from 'vue-router';
import { useStore } from 'vuex';

import InlineError from '@/components/base/form/InlineError.vue';
import Input from '@/components/base/form/Input.vue';
import ThemedButton from '@/components/base/ThemedButton.vue';
import { useDiscount } from '@/composables/useDiscount';

interface Props {
  redirect?: boolean;
  showLabel?: boolean;
  showPlaceholder?: boolean;
}
const props = withDefaults(defineProps<Props>(), {
  redirect: true,
  showLabel: true,
});

const router = useRouter();
const store = useStore();
const { applyDiscountCode } = useDiscount(store);
const discountCode = ref('');
const errorMessage = ref('');
const loading = ref(false);

const handleApplyDiscountCode = async () => {
  if (discountCode.value) {
    loading.value = true;
    try {
      await applyDiscountCode(discountCode.value);
      if (props.redirect) {
        router.push('/checkout/shipping');
      }
      discountCode.value = '';
    } catch (error: any) {
      errorMessage.value = 'Please enter a valid discount code';
    } finally {
      loading.value = false;
    }
  }
};

watch(discountCode, () => {
  if (errorMessage.value) {
    errorMessage.value = '';
  }
});
</script>

<template>
  <div data-test="discount-code-entry">
    <strong v-if="showLabel">Discount Code</strong>
    <div class="flex items-center mt-2 mb-6">
      <div>
        <Input
          v-model="discountCode"
          :invalid="!!errorMessage"
          :showPlaceholder="showPlaceholder"
          type="text"
          name="applyDiscountCode"
          id="applyDiscountCode"
          placeholder="Discount Code"
          aria-label="Discount Code"
          data-test="discount-code"
        />
        <InlineError
          :errors="[errorMessage]"
          fieldName="Discount Code"
          data-test="discount-code-validation-error"
        />
      </div>
      <ThemedButton
        class="ml-4"
        :isLoading="loading"
        theme="white"
        data-test="discount-code-submit"
        @click.native="handleApplyDiscountCode"
      >
        Apply
      </ThemedButton>
    </div>
  </div>
</template>
